import { aawzDate } from '@utils/dates/aawz-date'
import {
  DEFAULT_EVENT_DURATION_HOURS,
  LAST_MINUTE_OF_DAY,
  TIME_MASK,
} from '@modules/crm/constants/Calendar'
import { resolveClienteTarget } from '@modules/crm/utils/clienteTarget'
import { CRM_ACTIVITY_COLORS } from '@modules/crm/constants/Color'

// Mesma implementação de formatHourLabel — reexportado com o nome do domínio
// de agenda do CRM em vez de reimplementado.
export { formatHourLabel as padHour } from '@components/calendar-view/utils/dateUtils'

/**
 * Horário de fim padrão de um evento, a partir do horário de início.
 *
 * Trava em 23:59 quando o + 1h viraria o dia: os horários são comparados como
 * string em `_isStartAfterEnd`, então um fim 00:xx ficaria "menor" que o início
 * e bloquearia o save. A comparação lexicográfica só coincide com a cronológica
 * porque HH:mm aqui é sempre zero-padded — garantido pelo ClockTimePicker, que
 * é a única origem desses valores.
 *
 * @param {string} start - Horário de início no formato HH:mm
 * @returns {string} Horário HH:mm, ou '' se a entrada for inválida
 */
export const buildDefaultEndTime = (start) => {
  const parsed = aawzDate(start, TIME_MASK)
  if (!parsed.isValid()) return ''

  const end = parsed.add(DEFAULT_EVENT_DURATION_HOURS, 'hours').format(TIME_MASK)
  return end < start ? LAST_MINUTE_OF_DAY : end
}

/**
 * Converte DD/MM/YYYY para YYYYMMDD para ordenação cronológica.
 * localeCompare em DD/MM/YYYY ordena errado (ex: "31/01" > "05/02").
 * @param {string} dateStr - Data no formato DD/MM/YYYY
 * @returns {string} Data no formato YYYYMMDD
 */
export const toSortableDate = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return ''
  return dateStr.slice(6, 10) + dateStr.slice(3, 5) + dateStr.slice(0, 2)
}

/**
 * Ordena array de dias (objetos com propriedade `data` em DD/MM/YYYY) cronologicamente.
 * Mutação in-place.
 * @param {Array<{data: string}>} days
 */
export const sortCalendarDays = (days) => {
  days.sort((a, b) => {
    if (!a.data || !b.data) return 0
    return toSortableDate(a.data).localeCompare(toSortableDate(b.data))
  })
}

/**
 * Confere se `meta` (o `metadata` de uma atividade de calendário) corresponde
 * ao id de payload de uma atualização otimista (UPDATE_ACTIVITY_TIME/
 * UPDATE_ACTIVITY_DATE, store/mutations.js). Aceita o fallback `manual-` das
 * tarefas manuais de lead.
 * @param {Object} meta - atividade.metadata
 * @param {string|number} payloadId
 * @returns {boolean}
 */
export const matchesCalendarActivityId = (meta, payloadId) => {
  const atvId =
    meta.id_atividade || meta.id_etapa_atividade || meta.id_oportunidade
  if (!atvId) return false
  const atvIdStr = String(atvId)
  const payloadIdStr = String(payloadId)
  return atvIdStr === payloadIdStr || atvIdStr === 'manual-' + payloadIdStr
}

/**
 * Varre os buckets de dia do calendário (`state.calendar.data`) e retorna
 * TODAS as atividades cujo id (e, se informado, idLead) batem com o payload —
 * atividades de relacionamento geram duas entradas com o mesmo id (concluída
 * e pendente). Fonte única para as mutations de atualização otimista e para
 * quem precisa decidir (antes de despachar) se há o que atualizar.
 * @param {Array<{data: string, atividades: Array}>} days
 * @param {{id: string|number, idLead?: string|number}} payload
 * @returns {Array} atividades encontradas (mesma referência dos objetos no store)
 */
export const findCalendarActivitiesByPayload = (days, payload) => {
  const results = []
  if (!Array.isArray(days) || !payload) return results

  for (const day of days) {
    const atividades = day.atividades
    if (!Array.isArray(atividades)) continue

    for (const atv of atividades) {
      const meta = atv.metadata || {}
      if (!matchesCalendarActivityId(meta, payload.id)) continue
      if (
        payload.idLead &&
        meta.id_lead &&
        String(meta.id_lead) !== String(payload.idLead)
      )
        continue
      results.push(atv)
    }
  }
  return results
}

/**
 * Gera chave "YYYY-MM" a partir de mês e ano.
 * @param {number} month - Mês (1-12)
 * @param {number} year - Ano (ex: 2026)
 * @returns {string} Ex: "2026-03"
 */
export const buildMonthKey = (month, year) => {
  return year + '-' + String(month).padStart(2, '0')
}

/**
 * Retorna o último dia do mês em formato DD/MM/YYYY a partir de uma referência aawzDate.
 * Clona `ref` internamente — não muta a instância recebida.
 *
 * TODO: remover este workaround quando aawz-date for unificado em LOCAL time.
 * BUG: `aawzDate(...).endOf('month').format('DD/MM/YYYY')` retorna o dia 1 do mês seguinte
 * em timezones com offset negativo (BRT = UTC-3). Causa: `endOf.js` aplica `setHours(23,59,59,999)`
 * em horário LOCAL, mas `format.js` lê os componentes em UTC (`getUTCDate`). Em BRT, 23:59 local
 * cruza meia-noite UTC e o `getUTCDate` retorna o dia seguinte.
 * Workaround: navegar para o início do mês seguinte e subtrair 1 dia — mantém todas as
 * operações em horários 00:00 locais. Em offsets negativos (como BRT/UTC-3, alvo do bug)
 * isso garante que UTC e local não divergem de dia. Em offsets positivos (UTC+N), 00:00
 * local cai no dia anterior em UTC, mas o produto AAWZ não opera nesses fusos.
 * Quando aawz-date for migrado para LOCAL consistente, basta substituir por
 * `ref.endOf('month').format('DD/MM/YYYY')`.
 */
const formatMonthLastDay = (ref) =>
  aawzDate(ref.toDate())
    .add(1, 'months')
    .startOf('month')
    .subtract(1, 'days')
    .format('DD/MM/YYYY')

/**
 * Retorna data_inicio e data_fim do mês a partir de uma data DD/MM/YYYY.
 * @param {string} data - Data no formato DD/MM/YYYY
 * @returns {{ data_inicio: string, data_fim: string }}
 */
export const buildCurrentMonthWindow = (data) => {
  const ref = aawzDate(data, 'DD/MM/YYYY')
  const dataFim = formatMonthLastDay(ref)
  const dataInicio = ref.startOf('month').format('DD/MM/YYYY')
  return { data_inicio: dataInicio, data_fim: dataFim }
}

/**
 * Retorna data_inicio e data_fim a partir de uma monthKey "YYYY-MM".
 * @param {string} monthKey - Ex: "2026-03"
 * @returns {{ data_inicio: string, data_fim: string }}
 */
export const buildMonthWindow = (monthKey) => {
  const ref = aawzDate(monthKey + '-01', 'YYYY-MM-DD')
  return {
    data_inicio: ref.startOf('month').format('DD/MM/YYYY'),
    data_fim: formatMonthLastDay(ref),
  }
}

/**
 * Extrai horário HH:mm de uma data ISO.
 * @param {string|null} dataFim - Data ISO ou null
 * @returns {string|null}
 */
export const parseHorarioFim = (dataFim) => {
  if (!dataFim) return null
  const dt = aawzDate(dataFim)
  return dt && dt.isValid() ? dt.format('HH:mm') : null
}

/**
 * Monta o objeto metadata de um evento Google.
 * @param {Object} eventoGoogle - Evento bruto do Google
 * @param {string} eventType - Tipo do evento
 * @returns {Object}
 */
export const buildGoogleEventMetadata = (eventoGoogle, eventType) => {
  return {
    link: eventoGoogle.link || null,
    meet_link: eventoGoogle.meet_link || null,
    provedor: eventoGoogle.provedor || 'google',
    id_evento_provedor: eventoGoogle.id_evento_provedor || null,
    convidados: eventoGoogle.convidados || [],
    convidados_vinculados: eventoGoogle.convidados_vinculados || [],
    status: eventoGoogle.status || null,
    event_type: eventType,
  }
}

/**
 * Monta a lista de convidados de um evento Google para exibição no menu,
 * marcando cada um que é cliente/lead (via convidados_vinculados resolvido no
 * backend) com o chip e o rótulo do botão "abrir detalhes".
 * @param {Object} metadata - metadata do evento (convidados + convidados_vinculados)
 * @returns {Array<Object>} itens { email, hasLink, target?, chipLabel?, chipColor?, openLabel? }
 */
export const buildConvidadosView = (metadata) => {
  const meta = metadata || {}
  const vinculados = Array.isArray(meta.convidados_vinculados)
    ? meta.convidados_vinculados
    : []

  const linkByEmail = {}
  vinculados.forEach((convidado) => {
    const key = (convidado.email || '').trim().toLowerCase()
    if (key) linkByEmail[key] = convidado
  })

  const convidados = meta.convidados || []
  return convidados.map((email) => {
    const key = (email || '').trim().toLowerCase()
    const link = linkByEmail[key] || null

    if (!link) return { email, hasLink: false }

    const target = resolveClienteTarget(link)
    const isLead = target.mode === 'lead'
    const colorCfg = isLead
      ? CRM_ACTIVITY_COLORS.lead
      : CRM_ACTIVITY_COLORS.cliente

    return {
      email,
      hasLink: true,
      target,
      chipLabel: isLead ? 'Lead' : 'Cliente',
      chipColor: colorCfg.color,
      openLabel: isLead
        ? 'Abrir detalhes do Lead'
        : 'Abrir detalhes do Cliente',
    }
  })
}

/**
 * Cores por provedor para eventos externos.
 * Google = azul Google Calendar; Microsoft = azul Outlook.
 */
export const EXTERNAL_PROVIDER_COLORS = {
  google: { default: '#039BE5', alt: '#B3E5FC' },
  microsoft: { default: '#0078D4', alt: '#B3D7F2' },
}

/**
 * Formata um evento externo (Google ou Microsoft) bruto para o formato universal
 * do calendário CRM. O título padrão e a cor são derivados do provedor.
 * @param {Object} eventoExterno - Evento bruto (titulo, data_inicio, data_fim, provedor, etc.)
 * @returns {Object|null} Atividade formatada ou null se inválido
 */
export const formatarEventoGoogle = (eventoExterno) => {
  if (!eventoExterno || !eventoExterno.data_inicio) return null

  const dtInicio = aawzDate(eventoExterno.data_inicio)
  if (!dtInicio || !dtInicio.isValid()) return null

  const horarioFim = parseHorarioFim(eventoExterno.data_fim)
  const eventType = eventoExterno.event_type || 'default'
  const provedor = eventoExterno.provedor || 'google'
  const tituloPadrao =
    provedor === 'microsoft' ? 'Evento Outlook' : 'Evento Google Agenda'
  const colorSet =
    EXTERNAL_PROVIDER_COLORS[provedor] || EXTERNAL_PROVIDER_COLORS.google
  // Microsoft event_type é tipo de recorrência (singleInstance/occurrence/...),
  // não categoria — sempre cor default. Google mantém default vs alt por event_type.
  const isSecondary = provedor !== 'microsoft' && eventType !== 'default'
  const cor = isSecondary ? colorSet.alt : colorSet.default

  const atividade = {
    titulo: eventoExterno.titulo || tituloPadrao,
    descricao: eventoExterno.descricao || '',
    data: dtInicio.format('DD/MM/YYYY'),
    horario: dtInicio.format('HH:mm'),
    tipo: 'provedor-externo',
    tipo_atividade: '',
    cor,
    concluida: false,
    metadata: buildGoogleEventMetadata(eventoExterno, eventType),
  }

  if (horarioFim) {
    atividade.horario_fim = horarioFim
  }

  return atividade
}

/**
 * Monta a config do $showModal (v2) que pergunta se os convidados devem ser
 * notificados ao salvar/excluir um evento de agenda. Reaproveitado pela edição
 * (notificar atualização) e pela exclusão (notificar cancelamento) — o Google
 * só dispara e-mail quando sendUpdates=all.
 *
 * @param {Object} opts
 * @param {string} opts.title - Título do modal
 * @param {string} opts.text - Texto explicativo
 * @param {string} opts.skipLabel - Rótulo do botão que NÃO notifica
 * @param {string} opts.confirmLabel - Rótulo do botão que notifica
 * @param {Function} opts.onChoose - (notificar: boolean) => void|Promise
 * @param {string} [opts.type='neutral'] - Tipo visual do modal (neutral|danger|...)
 * @param {Object} [opts.skipClass] - Classe do botão "não notificar" (default primary outline + fitContent)
 * @param {Object} [opts.confirmClass] - Classe do botão "notificar" (default: usa o type)
 * @returns {Object} Config para $showModal
 */
export const buildNotifyGuestsModal = ({
  title,
  text,
  skipLabel,
  confirmLabel,
  onChoose,
  type = 'neutral',
  skipClass = { primary: true, outline: true, fitContent: true },
  confirmClass = undefined,
}) => ({
  v2: true,
  center: true,
  type,
  title,
  text,
  buttons: [
    { text: 'Cancelar' },
    {
      text: skipLabel,
      class: skipClass,
      // Fire-and-forget: não retorna a promise para o $showModal fechar na hora
      // (o the-feedback faz `await action()` antes de fechar).
      action: () => {
        onChoose(false)
      },
    },
    {
      text: confirmLabel,
      class: confirmClass,
      action: () => {
        onChoose(true)
      },
    },
  ],
})
